import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import Header from '../Header';
import DesktopSidebar from '../Sidebar/DesktopSidebar';
import MobileFooter from '../Sidebar/MobileFooter';
import UploadModal from '../UploadModal';
import NotificationBell from '../NotificationBell';
import { SearchSuggestionList } from '../SearchSuggestionList';
import { useLayout } from './useLayout';
import { LayoutProps } from './types';
import styles from './Layout.module.css';

const Layout = ({ currentUser }: LayoutProps) => {
  const {
    searchQuery,
    setSearchQuery,
    isMobile,
    searchOpen,
    setSearchOpen,
    isInputFocused,
    suggestions,
    searchRef,
    searchInputRef,
    selectedSuggestionIndex,
    handleLogoClick,
    handleSearchSubmit,
    handleSuggestionClick,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleSuggestionKeyDown,
    toggleSearch,
    shouldHideHeader,
    isVideoDetailPage
  } = useLayout();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const openUploadModal = () => setIsUploadModalOpen(true);
  const closeUploadModal = () => setIsUploadModalOpen(false);

  return (
    <div className={styles['layout-main-container']}>
      {/* PC版のみヘッダーを表示 (動画詳細・編集ページ以外) */}
      {!isMobile && !shouldHideHeader() && (
        <Header
          currentUser={currentUser || undefined}
          className={styles['layout-header']}
        />
      )}
      
      {/* PC版のみサイドバーを表示 */}
      {!isMobile && (
        <DesktopSidebar 
          currentUser={currentUser || undefined}
          className={styles['layout-sidebar']}
          onOpenUploadModal={openUploadModal}
        />
      )}
      
      {/* メインコンテンツエリア */}
      <main className={styles['layout-main']}>
          {/* SP版のみcontent-areaの上部にプロダクトアイコンと検索アイコンを表示 (動画詳細ページ以外) */}
          {isMobile && !shouldHideHeader() && (
            <div className={styles['layout-mobile-top-content']}>
              {/* ロゴ - 常に表示だが、検索枠が開いていると無効化 */}
              <div 
                className={`${styles['layout-mobile-logo']} ${searchOpen ? styles['layout-search-open'] : ''}`}
                onClick={searchOpen ? undefined : handleLogoClick}
              >
                <img
                  src="/asset/logo.svg"
                  alt="Logo"
                  className={styles['layout-mobile-logo-image']}
                  width="auto"
                  height="36"
                  loading="eager"
                />
              </div>
              
              {/* SP版の通知ベル - 検索が開いているときは非表示 */}
              {currentUser && !searchOpen && (
                <div className={styles['layout-mobile-notification-bell']}>
                  <NotificationBell isMobile={true} />
                </div>
              )}
              
              {/* 検索フォーム - 開閉に応じて幅が変化 */}
              <div 
                ref={searchRef}
                className={`${styles['layout-mobile-search-form']} ${searchOpen ? styles['layout-open'] : ''}`}
              >
                {/* 検索アイコン - 常に表示 */}
                <div 
                  className={styles['layout-mobile-search-icon']} 
                  onClick={() => toggleSearch()}
                >
                  <FaSearch />
                </div>
                
                {/* バツボタン - 検索が開いているときのみ表示 */}
                {searchOpen && (
                  <FaTimes 
                    className={styles['layout-close-icon']}
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // 入力がない場合は検索枠を閉じる
                      if (!searchQuery.trim()) {
                        toggleSearch();
                        return;
                      }
                      
                      // 入力がある場合は従来通りクリア
                      setSearchQuery('');
                    }}
                  />
                )}
                
                {/* 検索フォーム - 開いているときのみコンテンツを表示 */}
                {searchOpen && (
                  <form className={styles['layout-mobile-search-container']} onSubmit={handleSearchSubmit}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className={styles['layout-search-input']}
                      placeholder="検索..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                    />
                    
                    {/* サジェスト表示エリア */}
                    <SearchSuggestionList
                      suggestions={suggestions}
                      isVisible={isInputFocused}
                      selectedIndex={selectedSuggestionIndex}
                      onSuggestionClick={handleSuggestionClick}
                      onSuggestionKeyDown={handleSuggestionKeyDown}
                      testIdPrefix="layout-suggestion"
                      className={styles['layout-search-suggestions']}
                      itemClassName={styles['layout-suggestion-item']}
                      selectedItemClassName={styles['layout-suggestion-item--selected']}
                      renderSuggestionContent={(suggestion, index) => (
                        <>
                          <FaSearch className={styles['layout-suggestion-icon']} />
                          <span>{suggestion.display_text || suggestion.title || suggestion.value || ''}</span>
                        </>
                      )}
                    />
                  </form>
                )}
              </div>
            </div>
          )}
          
        <Outlet />
      </main>
      
      {/* SP版のみフッターナビゲーションを表示 */}
      {isMobile && (
        <MobileFooter 
          currentUser={currentUser || undefined}
          className={styles['layout-footer']}
          onOpenUploadModal={openUploadModal}
        />
      )}

      {isUploadModalOpen && (
        <UploadModal onClose={closeUploadModal} />
      )}
    </div>
  );
};

export default Layout; 
